/**
 * Return-URL helpers for the builder → identity-service handoff.
 *
 * Builder `/login` (and the other auth routes) only exist to bounce the
 * browser to auth.blazorly.com. Using those paths as the post-login
 * `?redirect=` target sends the user right back onto RedirectToAuth and
 * loops: auth → builder/login → auth → …
 */

export const AUTH_HANDOFF_PATHS = new Set([
	'/login',
	'/signup',
	'/register',
	'/forgot-password',
	'/reset-password',
]);

export function isAuthHandoffPath(pathname: string): boolean {
	return AUTH_HANDOFF_PATHS.has(pathname);
}

/**
 * Path to resume on the builder after a successful identity-service login.
 * Prefers ProtectedRoute's `state.from`, and never returns an auth-handoff
 * path (those would immediately bounce back to the identity service).
 */
export function builderReturnPath(location: {
	pathname: string;
	search?: string;
	state?: unknown;
}): string {
	const from = (
		location.state as { from?: { pathname?: string; search?: string } } | null | undefined
	)?.from;
	const pathname = from?.pathname ?? location.pathname;
	const search = from?.pathname !== undefined ? (from.search ?? '') : (location.search ?? '');
	if (
		!pathname.startsWith('/') ||
		pathname.startsWith('//') ||
		isAuthHandoffPath(pathname)
	) {
		return '/';
	}
	return `${pathname}${search}`;
}

export function builderReturnUrl(location: {
	pathname: string;
	search?: string;
	state?: unknown;
}): string {
	return `${window.location.origin}${builderReturnPath(location)}`;
}

/** Waitlist invitation ids are UUIDs issued by POST /api/admin/waitlist/:id/invite. */
const INVITE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

export function inviteIdFromSearch(search: string | undefined): string | null {
	const raw = search?.startsWith('?') ? search.slice(1) : (search ?? '');
	const invite = new URLSearchParams(raw).get('invite')?.trim() ?? '';
	return INVITE_ID.test(invite) ? invite : null;
}

/**
 * Identity-service URL for a builder auth bounce. `redirect` is the builder
 * path to resume after sign-in (never /signup — that would loop). `invite`
 * is forwarded separately so waitlist signup still works when public
 * registration is closed.
 */
export function identityHandoffUrl(
	authAppUrl: string,
	path: string,
	returnUrl: string,
	inviteId?: string | null,
): string {
	const params = new URLSearchParams();
	params.set('redirect', returnUrl);
	if (inviteId) params.set('invite', inviteId);
	const origin = authAppUrl.replace(/\/$/, '');
	const pathname = path.startsWith('/') ? path : `/${path}`;
	return `${origin}${pathname}?${params.toString()}`;
}
